import { Anchor, Box, Button, Divider, Group, Image, MantineStyleProps, Overlay, rem, Stack, Text, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { signInRedirectLocalStorageKey } from "shared/const";
import { useCustomer } from "shared/graphql/cust";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { fail } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { z } from "zod";

const fallback = "/messages" as const;

const Page = () => {
    const isMobile = useIsMobile();

    if (isMobile)
        return (
            <Stack justify="space-between" h="100vh">
                <Panel my="auto" />
                <Footer />
            </Stack>
        );

    return (
        <Group h="100vh" w="100vw" bg="brand" pos="relative">
            <Overlay gradient="linear-gradient(145deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0) 100%)" opacity={0.85} />
            <Stack justify="space-between" h="100vh" bg="brand">
                <Stack
                    flex={1}
                    justify="space-between"
                    m="xs"
                    style={{ backgroundColor: "var(--mantine-color-body)", borderRadius: "4px", zIndex: 1e4 }}
                >
                    <Panel my="auto" w={500} />
                    <Footer />
                </Stack>
            </Stack>
        </Group>
    );
};

const Panel = (props: MantineStyleProps) => {
    const { data: customer } = useCustomer();
    const auth = useAuth();
    // the library's typing is just incorrect
    const err = (auth?.error as unknown as { error_description: string })?.error_description;

    const submit = useMutation({
        throwOnError: false,
        mutationFn: (creds: { username: string; password: string }) =>
            auth.signinResourceOwnerCredentials(creds).then((it) => it ?? fail("Invalid credential")),
        onSuccess() {
            window.location.href = "/account/callback";
        },
        onError: () => {
            form.setFieldError("username", "Wrong Email or Password");
            form.setFieldError("password", "Wrong Email or Password");
        },
    });

    useEffect(() => {
        if (err == "Account is not fully set up") void auth.signinRedirect().catch(notify.catch);
    }, [err]);

    const form = useForm({
        initialValues: {
            username: "",
            password: "",
        },
    });

    function handleSubmit() {
        submit.mutateAsync(form.getValues()).catch(notify.catch);
    }

    return (
        <Stack p={rem(72)} justify="center" style={{ backgroundColor: "var(--mantine-color-body)", borderRadius: "4px" }} {...props}>
            <Image
                mx="auto"
                src={`${import.meta.env.VITE_API_BASE_URL}/customers/${customer.id}/icon`}
                fallbackSrc={"/bryx.svg"}
                radius="md"
                style={{ maxWidth: rem(100), maxHeight: rem(100) }}
            />
            <div style={{ textAlign: "center" }}>
                <Title order={5}>{customer.name}</Title>
                <Title order={6} c="dimmed">
                    Mass Notification System
                </Title>
            </div>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                }}
            >
                <Stack>
                    <TextInput label="Email" {...form.getInputProps("username")} />
                    <TextInput label="Password" type="password" {...form.getInputProps("password")} />
                    <Button variant="filled" loading={submit.isPending} w="100%" type="submit">
                        Sign In
                    </Button>
                </Stack>
            </form>
            <Divider />
            <Button
                mx="auto"
                size="xs"
                variant="transparent"
                c="brand"
                td="underline"
                onClick={() => void auth.signinRedirect().catch(notify.catch)}
            >
                More Sign In Options
            </Button>
        </Stack>
    );
};

const Footer = () => (
    <div>
        <Divider />
        <Box py="xl" bg="gray-accent" ta="center" style={{ borderRadius: "4px" }}>
            <Text size="sm">
                Powered by{" "}
                <Anchor fw="bold" td="none" href="https://bryx.com/bryx-mns">
                    Bryx Alerts
                </Anchor>
            </Text>
        </Box>
    </div>
);

/** @public */
export const Route = createFileRoute("/login")({
    validateSearch: z.object({
        redirect: z.string().optional().catch(""),
    }),
    loaderDeps: ({ search: { redirect } }) => ({ redirect }),
    loader: async ({ context, deps }) => {
        const auth = await context.auth;
        if (deps.redirect) localStorage.setItem(signInRedirectLocalStorageKey, deps.redirect);
        else localStorage.removeItem(signInRedirectLocalStorageKey);

        if (auth.isAuthenticated) throw redirect({ to: deps.redirect || fallback });
    },
    component: Page,
});
